/**
 * Session Timeout Manager
 * Automatically logs out users after a period of inactivity
 */

const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_DURATION = 5 * 60 * 1000; // 5 minutes warning before timeout

let timeoutId: NodeJS.Timeout | null = null;
let warningId: NodeJS.Timeout | null = null;
let lastActivityTime: number = Date.now();

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Callback functions for session events
 */
let onWarning: (() => void) | null = null;
let onTimeout: (() => void) | null = null;

/**
 * Reset the inactivity timer
 */
const resetTimer = () => {
  lastActivityTime = Date.now();

  // Clear existing timers
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  if (warningId) {
    clearTimeout(warningId);
  }

  // Set warning timer (5 minutes before timeout)
  warningId = setTimeout(() => {
    if (onWarning) {
      onWarning();
    }
  }, TIMEOUT_DURATION - WARNING_DURATION);

  // Set timeout timer (30 minutes)
  timeoutId = setTimeout(() => {
    if (onTimeout) {
      onTimeout();
    }
  }, TIMEOUT_DURATION);
};

/**
 * Handle user activity events
 */
const handleActivity = () => {
  const now = Date.now();
  const timeSinceLastActivity = now - lastActivityTime;

  // Debounce: only reset if it's been more than 1 second since last activity
  if (timeSinceLastActivity > 1000) {
    resetTimer();
  }
};

/**
 * Start monitoring user activity
 */
export const startSessionTimeout = (
  warningCallback: () => void,
  timeoutCallback: () => void
) => {
  onWarning = warningCallback;
  onTimeout = timeoutCallback;

  // Add event listeners for user activity
  ACTIVITY_EVENTS.forEach((event) => {
    window.addEventListener(event, handleActivity, { passive: true });
  });

  // Start the initial timer
  resetTimer();
};

/**
 * Stop monitoring user activity and clear timers
 */
export const stopSessionTimeout = () => {
  // Remove event listeners
  ACTIVITY_EVENTS.forEach((event) => {
    window.removeEventListener(event, handleActivity);
  });

  // Clear timers
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  if (warningId) {
    clearTimeout(warningId);
    warningId = null;
  }

  onWarning = null;
  onTimeout = null;
};

/**
 * Manually reset the session timeout timer
 */
export const resetSessionTimeout = () => {
  resetTimer();
};

/**
 * Get remaining time until timeout (in milliseconds)
 */
export const getRemainingTime = (): number => {
  const elapsed = Date.now() - lastActivityTime;
  const remaining = TIMEOUT_DURATION - elapsed;
  return Math.max(0, remaining);
};
